(() => {
  const MEASUREMENT_ID = "G-EP92WD45GK";
  const DIY_PATH = "/pages/design-your-bracelet";
  const ARTICLE_PATH_PREFIX = "/blogs/%E6%96%B0%E9%97%BB/";
  const SESSION_ID_KEY = "tce_diy_session_id";
  const STARTED_KEY = "tce_diy_builder_started";
  const SOURCE_ARTICLE_KEY = "tce_diy_source_article";
  const ATTRIBUTION_VERSION = 1;
  const ATTRIBUTION_TTL_MS = 30 * 60 * 1000;
  const INCOMING_ATTRIBUTION_TTL_MS = 5 * 60 * 1000;
  const ATTRIBUTION_QUERY_KEYS = Object.freeze([
    "tce_diy_source",
    "tce_diy_experiment",
    "tce_diy_attribution",
    "tce_diy_captured_at"
  ]);
  const ARTICLE_SOURCES = Object.freeze({
    "white-topaz-bracelet-care": Object.freeze({
      id: "CAL-001",
      experimentId: "",
      primaryQuery: "white topaz bracelet care"
    }),
    "diy-bracelet-bead-size-guide": Object.freeze({
      id: "CAL-002",
      experimentId: "",
      primaryQuery: "diy crystal bracelet bead size guide"
    }),
    "how-to-design-your-own-crystal-bracelet": Object.freeze({
      id: "NEXT-003",
      experimentId: "",
      primaryQuery: "how to design your own crystal bracelet"
    }),
    "crystal-bracelet-colour-ideas": Object.freeze({
      id: "NEXT-004",
      experimentId: "",
      primaryQuery: "crystal bracelet colour ideas"
    }),
    "diy-crystal-bracelet-length-guide": Object.freeze({
      id: "NEXT-005",
      experimentId: "",
      primaryQuery: "diy crystal bracelet length"
    }),
    "bracelet-spacer-bead-guide": Object.freeze({
      id: "NEXT-006",
      experimentId: "SEOEXP-NEXT-006-V1",
      primaryQuery: "bracelet spacer bead guide"
    }),
    "how-to-use-spacer-beads": Object.freeze({
      id: "NEXT-007",
      experimentId: "SEOEXP-NEXT-007-V1",
      primaryQuery: "how to use spacer beads"
    }),
    "running-rings-diy-bracelet-designs": Object.freeze({
      id: "NEXT-008",
      experimentId: "SEOEXP-NEXT-008-V1",
      primaryQuery: "bracelet running rings"
    }),
    "bracelet-component-guide": Object.freeze({
      id: "NEXT-009",
      experimentId: "SEOEXP-NEXT-009-V1",
      primaryQuery: "bracelet component guide"
    }),
    "ready-made-vs-custom-crystal-bracelets": Object.freeze({
      id: "NEXT-010",
      experimentId: "SEOEXP-NEXT-010-V1",
      primaryQuery: "ready made vs custom crystal bracelet"
    }),
    "gold-spacer-beads-vs-silver-spacer-beads": Object.freeze({
      id: "NEXT-011",
      experimentId: "SEOEXP-NEXT-011-V1",
      primaryQuery: "gold spacer beads bracelet"
    }),
    "how-to-arrange-beads-on-a-bracelet": Object.freeze({
      id: "NEXT-012",
      experimentId: "SEOEXP-NEXT-012-V1",
      primaryQuery: "how to arrange beads on a bracelet"
    }),
    "6mm-vs-8mm-crystal-bracelet-beads": Object.freeze({
      id: "NEXT-013",
      experimentId: "SEOEXP-NEXT-013-V1",
      primaryQuery: "6mm vs 8mm crystal bracelet beads"
    }),
    "minimal-crystal-bracelet-design-ideas": Object.freeze({
      id: "NEXT-014",
      experimentId: "SEOEXP-NEXT-014-V1",
      primaryQuery: "minimal crystal bracelet design"
    })
  });
  const EVENT_NAMES = new Set([
    "diy_builder_view",
    "diy_builder_start",
    "diy_bead_select",
    "diy_size_select",
    "diy_length_change",
    "diy_accessory_select",
    "diy_design_reorder",
    "diy_design_clear",
    "diy_cart_submit_click",
    "diy_cart_submit_result",
    "diy_support_link_click",
    "diy_article_to_builder_click"
  ]);
  const DORMANT_EVENTS = new Set(["diy_length_change"]);
  const script = document.currentScript;
  const context = {
    pageType: script?.dataset.pageType || ""
  };
  const debugMode = new URLSearchParams(window.location.search).get("tce_diy_debug") === "1";
  let analyticsAllowed = false;
  let builderViewSent = false;
  let builderViewRetryCount = 0;
  let builderViewRetryTimer = null;
  let builderObserver = null;

  function privacyApi() {
    return window.Shopify?.customerPrivacy;
  }

  function syncConsent() {
    analyticsAllowed = privacyApi()?.analyticsProcessingAllowed?.() === true;
    if (analyticsAllowed) {
      prepareIncomingAttribution();
      tryBuilderView();
      return;
    }
    clearAttribution();
    stripAttributionQuery();
  }

  function initConsent() {
    if (privacyApi()) {
      syncConsent();
      return;
    }
    if (typeof window.Shopify?.loadFeatures !== "function") return;
    window.Shopify.loadFeatures(
      [{ name: "consent-tracking-api", version: "0.1" }],
      (error) => {
        if (!error) syncConsent();
      }
    );
  }

  function sessionGet(key) {
    if (!analyticsAllowed) return "";
    try {
      return window.sessionStorage.getItem(key) || "";
    } catch (_error) {
      return "";
    }
  }

  function sessionSet(key, value) {
    if (!analyticsAllowed) return;
    try {
      window.sessionStorage.setItem(key, value);
    } catch (_error) {
      // Analytics still works without session persistence.
    }
  }

  function sessionRemove(key) {
    try {
      window.sessionStorage.removeItem(key);
    } catch (_error) {
      // No persistence is preferable to stale attribution.
    }
  }

  function diySessionId() {
    let value = sessionGet(SESSION_ID_KEY);
    if (value) return value;
    value = window.crypto?.randomUUID?.() || `diy-${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionSet(SESSION_ID_KEY, value);
    return value;
  }

  function safeUrl(value) {
    if (!value) return "";
    try {
      const url = new URL(value, window.location.origin);
      return `${url.origin}${url.pathname}`;
    } catch (_error) {
      return "";
    }
  }

  function safePath(value) {
    if (!value) return "";
    try {
      return new URL(value, window.location.origin).pathname;
    } catch (_error) {
      return "";
    }
  }

  function articleHandle(value) {
    const path = safePath(value);
    const handle = path.split("/").filter(Boolean).pop() || "";
    return /^[a-z0-9-]{1,100}$/.test(handle) ? handle : "";
  }

  function sourceDefinitionById(value) {
    if (!/^(?:CAL|NEXT)-\d{3}$/.test(value || "")) return null;
    return Object.entries(ARTICLE_SOURCES).find(([, source]) => source.id === value) || null;
  }

  function newAttributionId() {
    return window.crypto?.randomUUID?.()
      || `attr-${Date.now()}-${Math.random().toString(16).slice(2)}`;
  }

  function clearAttribution() {
    sessionRemove(SOURCE_ARTICLE_KEY);
  }

  function validAttribution(value) {
    if (!value || value.version !== ATTRIBUTION_VERSION) return null;
    const sourceEntry = sourceDefinitionById(value.article_id);
    if (!sourceEntry) return null;
    const [handle, source] = sourceEntry;
    if (value.article_handle !== handle) return null;
    if (value.article_path !== `${ARTICLE_PATH_PREFIX}${handle}`) return null;
    if ((value.experiment_id || "") !== source.experimentId) return null;
    if (!/^[a-z0-9-]{8,80}$/i.test(value.attribution_id || "")) return null;
    if (!Number.isFinite(value.captured_at) || !Number.isFinite(value.expires_at)) return null;
    if (Date.now() > value.expires_at || value.expires_at - value.captured_at > ATTRIBUTION_TTL_MS) {
      return null;
    }
    return {
      version: ATTRIBUTION_VERSION,
      article_id: source.id,
      article_handle: handle,
      article_path: `${ARTICLE_PATH_PREFIX}${handle}`,
      article_url: `${window.location.origin}${ARTICLE_PATH_PREFIX}${handle}`,
      experiment_id: source.experimentId,
      primary_query: source.primaryQuery,
      attribution_id: value.attribution_id,
      captured_at: value.captured_at,
      expires_at: value.expires_at
    };
  }

  function sourceArticle() {
    const value = sessionGet(SOURCE_ARTICLE_KEY);
    if (!value) return {};
    try {
      const source = validAttribution(JSON.parse(value));
      if (source) return source;
    } catch (_error) {
      // Invalid or expired source is removed below.
    }
    clearAttribution();
    return {};
  }

  function hasAttributionQuery(url) {
    return ATTRIBUTION_QUERY_KEYS.some((key) => url.searchParams.has(key));
  }

  function stripAttributionQuery() {
    if (window.location.pathname !== DIY_PATH) return;
    const url = new URL(window.location.href);
    if (!hasAttributionQuery(url)) return;
    ATTRIBUTION_QUERY_KEYS.forEach((key) => url.searchParams.delete(key));
    window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
  }

  function prepareIncomingAttribution() {
    if (window.location.pathname !== DIY_PATH || !analyticsAllowed) return;
    const url = new URL(window.location.href);
    if (!hasAttributionQuery(url)) {
      const referrerPath = safePath(document.referrer);
      if (sourceArticle().article_id && ![DIY_PATH, "/cart"].includes(referrerPath)) {
        clearAttribution();
      }
      return;
    }

    const articleId = url.searchParams.get("tce_diy_source") || "";
    const experimentId = url.searchParams.get("tce_diy_experiment") || "";
    const attributionId = url.searchParams.get("tce_diy_attribution") || "";
    const capturedAt = Number(url.searchParams.get("tce_diy_captured_at"));
    const sourceEntry = sourceDefinitionById(articleId);
    const incomingIsFresh = Number.isFinite(capturedAt)
      && capturedAt <= Date.now()
      && Date.now() - capturedAt <= INCOMING_ATTRIBUTION_TTL_MS;

    if (sourceEntry && incomingIsFresh) {
      const [handle, source] = sourceEntry;
      const candidate = validAttribution({
        version: ATTRIBUTION_VERSION,
        article_id: articleId,
        article_handle: handle,
        article_path: `${ARTICLE_PATH_PREFIX}${handle}`,
        article_url: `${window.location.origin}${ARTICLE_PATH_PREFIX}${handle}`,
        experiment_id: experimentId,
        primary_query: source.primaryQuery,
        attribution_id: attributionId,
        captured_at: capturedAt,
        expires_at: capturedAt + ATTRIBUTION_TTL_MS
      });
      if (candidate) sessionSet(SOURCE_ARTICLE_KEY, JSON.stringify(candidate));
      else clearAttribution();
    } else {
      clearAttribution();
    }
    stripAttributionQuery();
  }

  function trafficContext(source) {
    if (source.article_id || safePath(document.referrer).startsWith("/blogs/")) return "blog_referral";
    if (!document.referrer) return "direct";
    try {
      const host = new URL(document.referrer).hostname;
      if (/google\.|bing\.|yahoo\.|duckduckgo\.|ecosia\./i.test(host)) return "organic";
      return host === window.location.hostname ? "other" : "other";
    } catch (_error) {
      return "unknown";
    }
  }

  function globalParameters() {
    const source = sourceArticle();
    return {
      page_path: window.location.pathname,
      page_location: safeUrl(window.location.href),
      page_title: document.title,
      diy_session_id: diySessionId(),
      traffic_context: trafficContext(source),
      referrer_path: safePath(document.referrer),
      source_article_url: source.article_url || "",
      source_article_id: source.article_id || "",
      source_article_path: source.article_path || "",
      source_experiment_id: source.experiment_id || "",
      source_attribution_id: source.attribution_id || "",
      attribution_version: source.version ? `v${source.version}` : ""
    };
  }

  function cleanParameters(parameters) {
    return Object.fromEntries(
      Object.entries(parameters).map(([key, value]) => [key, value ?? ""])
    );
  }

  function emit(eventName, parameters = {}) {
    if (!EVENT_NAMES.has(eventName) || DORMANT_EVENTS.has(eventName)) return false;
    if (!analyticsAllowed || typeof window.gtag !== "function") return false;
    const payload = cleanParameters({
      ...globalParameters(),
      ...parameters,
      send_to: MEASUREMENT_ID,
      event_transport: "beacon",
      ...(debugMode ? { debug_mode: true } : {})
    });
    window.gtag("event", eventName, payload);
    if (eventName === "diy_cart_submit_result" && parameters.result === "success") {
      clearAttribution();
    }
    return true;
  }

  function emitBuilderStart(startAction) {
    const source = sourceArticle();
    const startScope = source.attribution_id
      ? `article:${source.attribution_id}`
      : `direct:${diySessionId()}`;
    if (sessionGet(STARTED_KEY) === startScope) return false;
    const sent = emit("diy_builder_start", { start_action: startAction || "unknown" });
    if (sent) sessionSet(STARTED_KEY, startScope);
    return sent;
  }

  function builderIsVisible(root) {
    const rect = root.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight;
  }

  function tryBuilderView() {
    if (builderViewSent || !analyticsAllowed || window.location.pathname !== DIY_PATH) return;
    const root = document.querySelector("[data-minestone-diy]");
    if (!root || (!window.location.hash.includes("design-your-bracelet") && !builderIsVisible(root))) return;
    if (!root.dataset.componentBindingTotal && builderViewRetryCount < 40) {
      builderViewRetryCount += 1;
      window.clearTimeout(builderViewRetryTimer);
      builderViewRetryTimer = window.setTimeout(tryBuilderView, 50);
      return;
    }
    builderViewSent = emit("diy_builder_view", {
      builder_anchor_visible: true,
      catalog_loaded: root.dataset.componentBindingTotal !== "" && root.dataset.componentBindingMissing === "0"
    });
    if (builderViewSent) {
      window.clearTimeout(builderViewRetryTimer);
      builderObserver?.disconnect();
    }
  }

  function observeBuilder() {
    if (window.location.pathname !== DIY_PATH) return;
    const root = document.querySelector("[data-minestone-diy]");
    if (!root) return;
    if ("IntersectionObserver" in window) {
      builderObserver = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) tryBuilderView();
      }, { threshold: 0.15 });
      builderObserver.observe(root);
    }
    tryBuilderView();
  }

  function storeArticleContext(link) {
    const handle = articleHandle(window.location.href);
    const source = ARTICLE_SOURCES[handle];
    const articlePath = safePath(window.location.href);
    if (!source || !analyticsAllowed || articlePath !== `${ARTICLE_PATH_PREFIX}${handle}`) return null;
    const capturedAt = Date.now();
    const articleContext = {
      version: ATTRIBUTION_VERSION,
      article_id: source.id,
      article_handle: handle,
      article_path: articlePath,
      article_url: safeUrl(window.location.href),
      experiment_id: source.experimentId,
      primary_query: source.primaryQuery,
      attribution_id: newAttributionId(),
      captured_at: capturedAt,
      expires_at: capturedAt + ATTRIBUTION_TTL_MS
    };
    sessionSet(SOURCE_ARTICLE_KEY, JSON.stringify(articleContext));
    const destination = new URL(link.href, window.location.origin);
    destination.searchParams.set("tce_diy_source", articleContext.article_id);
    if (articleContext.experiment_id) {
      destination.searchParams.set("tce_diy_experiment", articleContext.experiment_id);
    }
    destination.searchParams.set("tce_diy_attribution", articleContext.attribution_id);
    destination.searchParams.set("tce_diy_captured_at", String(capturedAt));
    link.href = destination.toString();
    return articleContext;
  }

  function handleTrackedLink(event) {
    const link = event.target.closest("a[href]");
    if (!link) return;
    const destination = new URL(link.href, window.location.origin);
    if (window.location.pathname === DIY_PATH && link.matches("[data-diy-support-link]")) {
      emit("diy_support_link_click", {
        destination_url: safeUrl(destination.href),
        destination_type: link.dataset.destinationType || "support_article",
        anchor_text: link.textContent.trim(),
        link_context: link.dataset.linkContext || "diy_support"
      });
      return;
    }
    const contextualArticleLink = context.pageType === "article"
      && destination.pathname === DIY_PATH
      && link.closest(".article-template__content");
    if (contextualArticleLink) {
      const articleContext = storeArticleContext(link);
      if (!articleContext) return;
      emit("diy_article_to_builder_click", {
        article_id: articleContext.article_id,
        article_url: articleContext.article_url,
        article_path: articleContext.article_path,
        experiment_id: articleContext.experiment_id,
        attribution_id: articleContext.attribution_id,
        article_primary_query: articleContext.primary_query,
        destination_url: safeUrl(destination.href),
        anchor_text: link.textContent.trim()
      });
    }
  }

  window.TCEDIYAnalytics = Object.freeze({
    emit,
    emitBuilderStart,
    allowedEvents: Object.freeze([...EVENT_NAMES]),
    dormantEvents: Object.freeze([...DORMANT_EVENTS]),
    measurementId: MEASUREMENT_ID,
    consentAllowed: () => analyticsAllowed,
    sourceAttribution: () => Object.freeze({ ...sourceArticle() }),
    debugMode
  });

  document.addEventListener("visitorConsentCollected", syncConsent);
  document.addEventListener("click", handleTrackedLink);
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", observeBuilder, { once: true });
  } else {
    observeBuilder();
  }
  initConsent();
})();
